// Import Packages
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Import Pages
import VariablePicker from '../VariablePicker/VariablePicker';
import { TextInput } from '../TextInput/NewTextInput';
import FieldLabel from '../FieldLabel/FieldLabel';

// Import Material UI
import makeStyles from '@material-ui/core/styles/makeStyles';
import MenuItem from '@material-ui/core/MenuItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';

// Import Recoil
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { saveAvailable, initialActions, changedFields } from "../../../recoil-store/recoil-state";

// Import Helpers
import { CheckIfFlowUpdated, isActionValueController } from '../../../helpers/helper';

const useStyles = makeStyles((theme) => ({
  root: { '&>*': { marginTop: 10 } },
  input: { width: '45%' },
  rowRange: {
    display: 'inline-block',
    width: '45%',
    '&:first-child': { paddingRight: 5 },
    '&:last-child': { paddingLeft: 5 }
  }
}));
const variableRegex = /##[^\s][^#]+?[^\s]##/

export function Loop({ action, inputName, variables, setOpenVariable, variableRender, }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const initialActionsData = useRecoilValue(initialActions);
  const setSaveAvailable = useSetRecoilState(saveAvailable);
  const [changedFieldsState, setChangeFields] = useRecoilState(changedFields);

  const label = 'CONTENT.CONFIGURATION-FORM.REPEATABLE.REPEAT.';
  const [iterator, setIterator] = useState('');
  const [loopThrough, setLoopThrough] = useState('ALL_ROWS');
  const [fromRow, setFromRow] = useState(0);
  const [toRow, setToRow] = useState(0);
  const [times, setTimes] = useState(0);

  useEffect(() => {
    if (!action.fields.repeatSource) { action.fields.repeatSource = { value: '', isRequired: true }; }
    else { setIterator(action.fields.repeatSource.value); }
    if (!action.fields.repeatType) { action.fields.repeatType = { value: 'ALL_ROWS', isRequired: false }; }
    else { setLoopThrough(action.fields.repeatType.value ? action.fields.repeatType.value : 'ALL_ROWS'); }
    if (!action.fields.fromRow) { action.fields.fromRow = { value: 0, isRequired: false }; }
    else { setFromRow(action.fields.fromRow.value); }
    if (!action.fields.toRow) { action.fields.toRow = { value: 0, isRequired: false }; }
    else { setToRow(action.fields.toRow.value); }
    if (!action.fields.times) { action.fields.times = { value: 0, isRequired: false }; }
    else { setTimes(action.fields.times.value); }
  }, [action, variables]);

  const handleIteratorChange = (event) => {
    setIterator(event.target.value);
    setLoopThrough('ALL_ROWS');
    action.fields.repeatSource.value = event.target.value;


    if (event.target.value !== 'FOR_EACH_N_TIMES') {
      delete action.fields.howManyTimes;
      delete action.fields.startFrom;
    } else {
      delete action.fields.listData;
    }

    // Required fields removed
    if (event.target.value === 'EXCEL_EACH_ROW') {
      delete action.fields.listData;
    } else {
      delete action.fields.excelSource;
      delete action.fields.fromRow;
      delete action.fields.toRow;
      delete action.fields.repeatType;
    }
    delete action.fields.set;

    const field = initialActionsData[action.id]?.fields.repeatSource;
    const oldValue = !field ? undefined : field.value;
    CheckIfFlowUpdated('repeatSource', action, oldValue, event.target.value, setSaveAvailable, setChangeFields, changedFieldsState);
  };

  const handleLoopThroughChange = (event) => {
    setLoopThrough(event.target.value);
    action.fields.repeatType.value = event.target.value;

    const field = initialActionsData[action.id]?.fields.repeatType;
    const oldValue = !field ? undefined : field.value;
    CheckIfFlowUpdated('repeatType', action, oldValue, event.target.value, setSaveAvailable, setChangeFields, changedFieldsState);
  };

  const handleTimesChange = (event) => {
    if (event.target.value >= 0) {
      setTimes(event.target.value);
      action.fields.times.value = event.target.value;
    }
  };

  const handleFromRowChange = (event) => {
    if (event.target.value >= 0) {
      setFromRow(event.target.value);
      action.fields.fromRow.value = event.target.value;

      const field = initialActionsData[action.id]?.fields.fromRow;
      const oldValue = !field ? undefined : field.value;
      CheckIfFlowUpdated('fromRow', action, oldValue, event.target.value, setSaveAvailable, setChangeFields, changedFieldsState);
    }
  };

  const handleToRowChange = (event) => {
    if (event.target.value >= 0) {
      setToRow(event.target.value);
      action.fields.toRow.value = event.target.value;

      const field = initialActionsData[action.id]?.fields.toRow;
      const oldValue = !field ? undefined : field.value;
      CheckIfFlowUpdated('toRow', action, oldValue, event.target.value, setSaveAvailable, setChangeFields, changedFieldsState);
    }
  };

  return (
    <div className={classes.root}>
      <div className="conf-field-container">
        <FieldLabel name={t(label + 'REPEAT_SOURCE')} is_required />
        <FormControl fullWidth style={{ width: '90%' }}>
          <Select variant='outlined' defaultValue='' value={iterator} onChange={handleIteratorChange}>
            <MenuItem value=''>
              <em>{t(label + 'CHOOSE_AN_ITERATOR')}</em>
            </MenuItem>
            <ListSubheader value=''>Excel</ListSubheader>
            <MenuItem className={classes.formControl} value={'EXCEL_EACH_ROW'}>
              {t(label + 'EXCEL-EACH-ROW')}
            </MenuItem>
            <ListSubheader value=''>{t(label + 'VARIABLE')}</ListSubheader>
            <MenuItem className={classes.formControl} value={'VARIABLE_EACH_DATA'}>
              {t(label + 'VARIABLE_EACH_DATA')}
            </MenuItem>
            <ListSubheader value=''>{t(label + 'MAIL')}</ListSubheader>
            <MenuItem className={classes.formControl} value={'MAIL_EACH_VARIABLE'}>
              {t(label + 'MAIL_EACH_VARIABLE')}
            </MenuItem>
            <ListSubheader value=''>{t(label + 'N-TIMES')}</ListSubheader>
            <MenuItem className={classes.formControl} value={'FOR_EACH_N_TIMES'}>
              {t(label + 'FOR-EACH-N-TIMES')}
            </MenuItem>
            {/* <ListSubheader value=''>{t(label + 'REPEAT')}</ListSubheader><MenuItem className={classes.formControl} value={'LOOP'}>{t(label + 'FOR_N_TIMES')}</MenuItem> */}
          </Select>
        </FormControl>
      </div>
      {iterator === 'EXCEL_EACH_ROW' && (
        <div className={classes.formControl}>
          <div className="conf-field-container">
            <FieldLabel name={t('CONTENT.CONFIGURATION-FORM.EXCEL.COPY.EXCEL-SOURCE')} is_required />
            <VariablePicker action={action} inputName="excelSource" variables={variables}
              setOpenVariable={setOpenVariable} filter="data table" isRequired hideRequired />
            <small className="conf-field-desc"><em>{t('CONTENT.CONFIGURATION-FORM.VARIABLE-HELPER', { type: 'data table' })}</em></small>
          </div>
          <div className="conf-field-container">
            <FieldLabel name={t(label + 'LOOP_THROUGH')} />
            <FormControl variant='outlined' fullWidth style={{ width: '90%' }}>
              <Select value={loopThrough} onChange={handleLoopThroughChange}>
                <MenuItem value=''>
                  <em>None</em>
                </MenuItem>
                <MenuItem value={'ALL_ROWS'}>{t(label + 'ALL-ROWS')}</MenuItem>
                <MenuItem value={'SPECIFIC_ROWS'}>
                  {t(label + 'SPECIFIC_ROWS')}
                </MenuItem>
              </Select>
            </FormControl>
          </div>
          {loopThrough === 'SPECIFIC_ROWS' && (
            <div className="conf-field-container">
              <div className={classes.rowRange}>
                <FieldLabel name={t(label + 'FROM-ROW')} is_required />
                <TextField variant='outlined' type='number' onChange={handleFromRowChange} value={fromRow} fullWidth
                />
              </div>
              <div className={classes.rowRange}>
                <FieldLabel name={t(label + 'TO-ROW')} is_required />
                <TextField variant='outlined' type='number' onChange={handleToRowChange} value={toRow} fullWidth
                />
              </div>
            </div>
          )}
          <div className="conf-field-container">
            <FieldLabel name={t(label + 'ASSIGN_TO_VARIABLE')} is_required />
            <VariablePicker action={action} inputName="set" variables={variables} setOpenVariable={setOpenVariable} filter='array'
              isRequired variableRender={variableRender} hideRequired />
            <small className="conf-field-desc"><em>{t('CONTENT.CONFIGURATION-FORM.VARIABLE-HELPER', { type: 'array' })}</em></small>
          </div>
          <div className="conf-field-container">
            <FieldLabel name={t(label + 'ASSIGN-INDEX')} />
            <VariablePicker action={action} inputName="setIndex" variables={variables} setOpenVariable={setOpenVariable} filter="number" />
            <small className="conf-field-desc"><em>{t(label + "ASSIGN-INDEX-INFO")}</em></small>
          </div>
        </div>
      )}
      {/* {iterator === 'LOOP' && (
        <div className={classes.formControl}>
          <div>{t(label + 'TIMES')}</div>
          <FieldLabel name={t(label + 'REPEAT_SOURCE')} is_required />
          <TextField variant='outlined' type='number' onChange={handleTimesChange} value={times} fullWidth />
        </div>
      )} */}
      {iterator && iterator !== '' && iterator === 'VARIABLE_EACH_DATA' && (
        <>
          <div className="conf-field-container">
            <div className="conf-field-container">
              <FieldLabel name={t(label + 'DATA-OF-THE-LIST')} is_required />
              <TextInput action={action} inputName="listData" variables={variables} setOpenVariable={setOpenVariable} variableRender={variableRender} isRequired hideRequired />
            </div>
            <div className="conf-field-container">
              <FieldLabel name={t(label + 'ASSIGN_TO_VARIABLE')} is_required />
              <VariablePicker action={action} inputName="set" variables={variables} setOpenVariable={setOpenVariable} variableRender={variableRender} isRequired hideRequired />
              <small className="conf-field-desc"><em>{t(label + 'ASSIGN-TO-VARIABLE-SHORT-INFO')}</em></small>
            </div>
            <div className={classes.formControl}>
              <FieldLabel name={t(label + 'ASSIGN-INDEX')} />
              <VariablePicker action={action} inputName="setIndex" variables={variables} setOpenVariable={setOpenVariable} filter="number" />
              <small className="conf-field-desc"><em>{t(label + "ASSIGN-INDEX-INFO")}</em></small>
            </div>
          </div>
        </>
      )}

      {iterator === 'FOR_EACH_N_TIMES' && (
        <>
          <div className="conf-field-container">

            <div className="conf-field-container">
              <FieldLabel name={t(label + 'HOW-MANY-TIMES')} is_required />
              <TextInput action={action} inputName="howManyTimes" variables={variables} setOpenVariable={setOpenVariable}
                variableRender={variableRender} isRequired hideRequired defaultValue={"10"} />
              <small className="conf-field-desc"><em>{t(label + 'HOW-MANY-TIMES-INFO')}</em></small>
            </div>

            <div className="conf-field-container">
              <FieldLabel name={t(label + 'START-FROM')} is_required />
              <TextInput action={action} inputName="startFrom" variables={variables} setOpenVariable={setOpenVariable}
                variableRender={variableRender} isRequired hideRequired defaultValue={"1"} />
              <small className="conf-field-desc"><em>{t(label + 'START-FROM-INFO')}</em></small>
            </div>

            <div className={classes.formControl}>
              <FieldLabel name={t(label + 'ASSIGN_TO_VARIABLE')} />
              <VariablePicker action={action} inputName="set" variables={variables} setOpenVariable={setOpenVariable} filter="number" />
              <small className="conf-field-desc"><em>{t(label + 'ASSIGN-TO-VARIABLE-INFO')}</em></small>
            </div>

          </div>
        </>
      )}

      {iterator === 'MAIL_EACH_VARIABLE' ? (
        <>
          <div className="conf-field-container">
            <div className="conf-field-container">
              <FieldLabel name={t(label + 'DATA-OF-THE-LIST')} is_required />
              <VariablePicker action={action} inputName="listData" variables={variables} setOpenVariable={setOpenVariable} variableRender={variableRender} filter="array" isRequired hideRequired />
              <small className="conf-field-desc"><em>{t('CONTENT.CONFIGURATION-FORM.VARIABLE-HELPER', { type: 'array' })}</em></small>
            </div>
            <div className="conf-field-container">
              <FieldLabel name={t(label + 'ASSIGN_TO_VARIABLE')} is_required />
              <VariablePicker action={action} inputName="set" variables={variables} setOpenVariable={setOpenVariable} variableRender={variableRender} filter="object" isRequired hideRequired />
              <small className="conf-field-desc"><em>{t(label + "ASSIGN-TO-VARIABLE-SHORT-INFO")}</em></small>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}